import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import MapboxMap, { Source, Layer } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { ArrowRight, ExternalLink } from "lucide-react";
import { getMyTrip } from "@/lib/trip.functions";
import { getAdminOverview, getTripRoute } from "@/lib/admin.functions";
import { MAPBOX_TOKEN } from "@/lib/mapbox";
import { PageHeader } from "@/components/admin/PageHeader";
import { RoavaCard, StatusChip, SecondaryButton, TextButton } from "@/components/roava";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => pageMeta({ title: 'Trip Admin Overview — Roava by WGP Apps', description: 'Manage your trip: members, invites, media library, and route map.', path: '/admin' }),
  component: AdminOverview,
});

const COLORS = ["#4f46e5", "#0891b2", "#b45309", "#15803d", "#9333ea", "#be123c", "#0f766e", "#c2410c"];

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
}

function AdminOverview() {
  const tripFn = useServerFn(getMyTrip);
  const ovFn = useServerFn(getAdminOverview);
  const routeFn = useServerFn(getTripRoute);
  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id;
  const { data: overview } = useQuery({
    queryKey: ["admin-overview", tripId],
    queryFn: () => ovFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });
  const { data: routeData } = useQuery({
    queryKey: ["admin-route", tripId],
    queryFn: () => routeFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const stats = overview?.stats ?? [];
  const routes = routeData?.routes ?? [];
  const firstPoint = routes.flatMap((r) => r.points)[0];

  const totals = stats.reduce(
    (acc, s) => {
      acc.uploaded += s.uploaded;
      acc.queued += s.queued;
      acc.failed += s.failed;
      acc.bytes += s.totalBytes;
      if (s.uploaded + s.queued + s.pings > 0) acc.active += 1;
      return acc;
    },
    { uploaded: 0, queued: 0, failed: 0, bytes: 0, active: 0 },
  );

  const nameById = new Map(stats.map((s) => [s.userId, s.displayName]));

  return (
    <main className="mx-auto max-w-6xl px-5 md:px-8 py-6 md:py-8">
      <PageHeader
        title="Overview"
        subtitle={tripData?.trip?.name ? `${tripData.trip.name} · live operations` : "Live operations"}
      />

      {/* KPI strip */}
      <RoavaCard flush className="mb-8 overflow-hidden">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[var(--roava-border)]">
          <Kpi label="Uploaded" value={totals.uploaded} />
          <Kpi label="In queue" value={totals.queued} />
          <Kpi label="Failed" value={totals.failed} tone={totals.failed > 0 ? "danger" : "default"} />
          <Kpi label="Travelers active" value={`${totals.active}/${stats.length}`} />
        </div>
      </RoavaCard>

      {/* Travelers table */}
      <section className="mb-8">
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-[15px] font-semibold tracking-tight text-[var(--roava-text)]">
            Travelers
          </h2>
          <p className="text-[12px] text-[var(--roava-text-muted)] tabular">
            {(totals.bytes / 1024 / 1024).toFixed(1)} MB total
          </p>
        </div>

        <RoavaCard flush className="overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.6fr_repeat(4,0.6fr)_0.8fr_auto] px-4 py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--roava-text-muted)] border-b border-[var(--roava-border)]">
            <span>Traveler</span>
            <span className="text-right">Uploaded</span>
            <span className="text-right">Queued</span>
            <span className="text-right">Failed</span>
            <span className="text-right">Pings</span>
            <span className="text-right">Size</span>
            <span className="text-right pl-3">Actions</span>
          </div>
          {stats.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[var(--roava-text-muted)]">
              No travelers yet.
            </div>
          ) : (
            stats.map((s) => (
              <div
                key={s.userId}
                className="grid grid-cols-2 md:grid-cols-[1.6fr_repeat(4,0.6fr)_0.8fr_auto] gap-y-1 items-center px-4 py-3 border-b border-[var(--roava-border)] last:border-0 hover:bg-accent/40"
              >
                <div className="col-span-2 md:col-span-1 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-secondary text-[11px] font-medium grid place-items-center text-secondary-foreground">
                    {initials(s.displayName) || "·"}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-medium leading-tight truncate text-[var(--roava-text)]">
                      {s.displayName}
                    </p>
                    <p className="text-[11px] text-[var(--roava-text-muted)] md:hidden tabular">
                      {s.uploaded} up · {s.queued} q · {s.failed} fail · {s.pings} pings
                    </p>
                  </div>
                </div>
                <Cell value={s.uploaded} />
                <Cell value={s.queued} muted={s.queued === 0} />
                <Cell value={s.failed} tone={s.failed > 0 ? "danger" : "muted"} />
                <Cell value={s.pings} muted={s.pings === 0} />
                <Cell value={`${(s.totalBytes / 1024 / 1024).toFixed(1)} MB`} muted />
                <div className="hidden md:flex justify-end pl-3 gap-1">
                  <SecondaryButton size="sm" asChild className="h-7 px-2 text-[11.5px]">
                    <Link to="/admin/map" search={{ user: s.userId }}>
                      Route <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </SecondaryButton>
                </div>
              </div>
            ))
          )}
        </RoavaCard>
      </section>

      {/* Map */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-[15px] font-semibold tracking-tight text-[var(--roava-text)]">
            Group route
          </h2>
          <TextButton asChild className="text-[12.5px]" tone="muted">
            <Link to="/admin/map">
              Open full route <ExternalLink className="ml-1 inline h-3 w-3" />
            </Link>
          </TextButton>
        </div>

        <RoavaCard flush className="p-4">
          {routes.length > 0 ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
              {routes.map((r, i) => (
                <StatusChip key={r.userId} variant="neutral">
                  <span
                    className="h-2 w-2 rounded-full mr-1.5"
                    style={{ background: COLORS[i % COLORS.length] }}
                  />
                  {nameById.get(r.userId) ?? "Traveler"}
                  <span className="ml-1 opacity-70 tabular">· {r.points.length}</span>
                </StatusChip>
              ))}
            </div>
          ) : null}

          <div className="h-[420px] rounded-[16px] overflow-hidden border border-[var(--roava-border)]">
            <MapboxMap
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={{
                longitude: firstPoint?.lng ?? 103.8467,
                latitude: firstPoint?.lat ?? 46.8625,
                zoom: firstPoint ? 6 : 4,
              }}
              mapStyle="mapbox://styles/mapbox/light-v11"
              style={{ width: "100%", height: "100%" }}
            >
              {routes.map((r, i) => (
                <Source
                  key={r.userId}
                  id={`r-${r.userId}`}
                  type="geojson"
                  data={{
                    type: "Feature",
                    properties: {},
                    geometry: {
                      type: "LineString",
                      coordinates: r.points.map((p) => [p.lng, p.lat]),
                    },
                  }}
                >
                  <Layer
                    id={`l-${r.userId}`}
                    type="line"
                    paint={{ "line-color": COLORS[i % COLORS.length], "line-width": 2.5 }}
                  />
                </Source>
              ))}
            </MapboxMap>
          </div>
        </RoavaCard>
      </section>
    </main>
  );
}

function Kpi({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "danger";
}) {
  return (
    <div className="px-5 py-4">
      <p className="text-[11px] uppercase tracking-[0.12em] text-[var(--roava-text-muted)]">
        {label}
      </p>
      <p
        className={[
          "mt-1.5 text-[26px] font-semibold leading-none tabular",
          tone === "danger" ? "text-[var(--roava-red)]" : "text-[var(--roava-text)]",
        ].join(" ")}
      >
        {value}
      </p>
    </div>
  );
}

function Cell({
  value,
  tone = "default",
  muted = false,
}: {
  value: number | string;
  tone?: "default" | "danger" | "muted";
  muted?: boolean;
}) {
  return (
    <span
      className={[
        "text-right text-[13.5px] tabular hidden md:block",
        tone === "danger"
          ? "text-[var(--roava-red)] font-medium"
          : muted
            ? "text-[var(--roava-text-muted)]"
            : "text-[var(--roava-text)]",
      ].join(" ")}
    >
      {value}
    </span>
  );
}
